import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { getAxiosInstance } from '../../utils/axiosInstance';
import { useParams, useLocation } from 'react-router-dom';
import Input2 from '../../NecttosComp/Input/Input2';
import Button from '../../NecttosComp/Button/Button';
import { OLoading } from '../../static/svg/icons';
import toast, { Toaster } from 'react-hot-toast';

function loadRazorpayScript() {
	return new Promise((resolve) => {
		if (window.Razorpay) {
			resolve(true);
			return;
		}
		const script = document.createElement('script');
		script.src = 'https://checkout.razorpay.com/v1/checkout.js';
		script.onload = () => {
			resolve(true);
		};
		script.onerror = () => {
			resolve(false);
		};
		document.body.appendChild(script);
	});
}

const getForm = async ({ queryKey }) => {
	const [_key, formId] = queryKey;

	try {
		const instance = getAxiosInstance();
		const { data, status } = await instance.get('/college/forms/getForm', {
			params: { formId },
		});

		if (status === 200) {
			return data?.data;
		} else {
			throw new Error(data?.message || 'Failed to fetch form details.');
		}
	} catch (error) {
		if (error.response?.status === 404) {
			toast.error('Form not found');
			return { acceptResponse: false, formData: [], title: 'Form not found', description: 'The form you are looking for does not exist.', error: true };
		}
		throw new Error('API Error');
	}
};

function Forms() {
	const { formId } = useParams();
	const location = useLocation();
	const isAlredySubmitted = localStorage.getItem('isAlreadySubmitted');

	const printRef = React.useRef(null);

	const [formData, setFormData] = useState(() => {
		// If coming from handleEdit, location.state will have the response data
		if (location.state && typeof location.state === 'object') {
			return { ...location.state };
		}
		return {};
	});
	const [loading, setLoading] = useState(false);
	const { data: form, isLoading, error } = useQuery(['formId', formId], getForm);

	const handleSubmit = async () => {
		setLoading(true);
		const allDataFilled = form?.formData?.every((item) => formData?.[item?._id] !== '' && formData?.[item?._id] !== undefined);
		if (!allDataFilled) {
			toast.error('Please fill all the fields');
			setLoading(false);
			return;
		}
		const instance = getAxiosInstance();
		try {
			const { data, status } = await instance.post('/college/forms/submitForm', {
				formId: form.formId,
				collegeId: form.collegeId,
				formData: form?.formData?.reduce((acc, item) => {
					if (formData[item._id]?.toLowerCase?.().includes('other')) {
						acc[item._id] = formData[`${item._id}_other`] || formData[item._id];
					} else {
						acc[item._id] = formData[item._id];
					}
					return acc;
				}, {}),
			});
			if (status === 200) {
				setFormData({});
				toast.success('Form submitted successfully');
				localStorage.setItem('isAlreadySubmitted', JSON.stringify([...JSON.parse(isAlredySubmitted || '[]'), formId]));
				renderPDF(data?._id);
				return data?.data;
			} else {
				throw new Error(data?.message || 'Failed to submit form.');
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleStartPayment = async () => {
		const allDataFilled = form?.formData?.every((item) => formData?.[item?._id] !== '' && formData?.[item?._id] !== undefined);
		if (!allDataFilled) {
			toast.error('Please fill all the fields');
			return;
		}

		setLoading(false);
		try {
			const scriptLoaded = await loadRazorpayScript();
			if (!scriptLoaded) {
				toast.error('Failed to load payment gateway. Please try again.');
				setLoading(false);
				return;
			}
			const instance = getAxiosInstance();
			const { data, status } = await instance.post('/college/forms/create-order', {
				formId: form?.formId,
				collegeId: form?.collegeId,
				contact: formData?.phoneNumber || formData?.email,
				name: formData?.name,
			});
			if (status === 200) {
				const { receipt, amount, currency } = data?.order;
				const options = {
					amount: amount * 100,
					currency: currency,
					name: form?.collegeName,
					description: 'Online Application Fee',
					image: form?.logo,
					order_id: data?.order?.id,
					handler: async (response) => {
						const { razorpay_order_id, razorpay_payment_id, razorpay_signature } = response;
						try {
							setLoading(true);
							const { data, status } = await instance.post('/college/forms/payment', {
								orderDetails: { razorpay_order_id, razorpay_payment_id, razorpay_signature },
								formId: form?.formId,
								collegeId: form?.collegeId,
								contact: formData?.phoneNumber || formData?.email,
								name: formData?.name,
								billAmount: form?.amount,
								receipt: receipt,
								formData: form?.formData?.reduce((acc, item) => {
									if (formData[item._id]?.toLowerCase?.().includes('other')) {
										acc[item._id] = formData[`${item._id}_other`] || formData[item._id];
									} else {
										acc[item._id] = formData[item._id];
									}
									return acc;
								}, {}),
							});
							if (status === 200) {
								setFormData({});
								toast.success('Form submitted successfully');
								localStorage.setItem('isAlreadySubmitted', JSON.stringify([...JSON.parse(isAlredySubmitted || '[]'), formId]));
								renderPDF(data?._id);
								return data?.data;
							} else {
								throw new Error(data?.message || 'Failed to submit form.');
							}
						} catch (error) {
							console.error(error);
							toast.error('Payment verification failed');
						} finally {
							setLoading(false);
						}
					},
					prefill: {
						name: formData?.name,
						email: formData?.email || '',
						contact: formData?.phoneNumber || formData?.email,
					},
					theme: {
						color: '#3399cc',
					},
				};
				const rzp = new window.Razorpay(options);
				rzp.open();
				rzp.on('payment.failed', function (response) {
					toast.error('Payment failed');
					console.error('Payment failed:', response);
				});
			} else {
				throw new Error(data?.message || 'Failed to create order.');
			}
		} catch (error) {
			console.error(error);
			toast.error('API Error');
		} finally {
			setLoading(false);
		}
	};

	const renderPDF = (responseId) => {
		window.location.href = `/response/${responseId}`;
	};

	if (isLoading || loading)
		return (
			<div className='flex items-center justify-center min-h-[60vh]'>
				<OLoading styles={{ height: 30, width: 30 }} />
			</div>
		);
	if (error) return <div className='flex items-center justify-center min-h-[60vh] text-red-500 text-lg'>Error: {error.message}</div>;

	if (isAlredySubmitted) {
		const filledIds = JSON.parse(isAlredySubmitted);
		if (filledIds.includes(formId)) {
			return (
				<div className='bg-slate-50 dark:bg-gray-900 w-full h-full flex items-center justify-center min-h-screen'>
					<div className='max-w-xl w-full mx-auto p-6'>
						<div className='bg-slate-50 dark:bg-[#1e1e28] rounded-2xl shadow-xl p-10 text-center transition-colors duration-300 flex flex-col items-center gap-4'>
							<svg className='w-14 h-14 text-green-500 mb-2' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
								<path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
							</svg>
							<h1 className='text-2xl font-extrabold text-gray-900 dark:text-white'>Submission Saved Successfully</h1>
							<p className='mt-1 text-lg font-medium text-gray-700 dark:text-gray-300'>You have already submitted this form.</p>
						</div>
					</div>
				</div>
			);
		}
	}

	return (
		<div className='min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-900 overflow-y-auto'>
			<Toaster />

			<div className='w-full  mx-auto p-6 overflow-y-auto h-full'>
				<div className='bg-slate-50 dark:bg-[#1e1e28] rounded-2xl shadow-xl p-8 flex flex-col gap-8 printable' id='print' ref={printRef}>
					<header className='flex items-center gap-6 md:flex-row flex-col'>
						{!form?.error ? <img src={form?.logo} alt={`${form?.collegeName} logo`} className='w-20 h-20 object-contain rounded-xl border border-gray-300 dark:border-gray-700' /> : <h1 className='text-red-500 text-3xl font-bold'>404</h1>}
						<div>
							<h1 className='text-3xl font-extrabold text-gray-900 dark:text-white text-center'>{form?.collegeName}</h1>
							<p className='mt-1 text-lg font-medium text-gray-700 dark:text-gray-300  text-center'>{form?.address}</p>
							<p className='text-sm font-semibold text-indigo-600 dark:text-indigo-400 text-center'>{form?.accreditation}</p>
							{form?.website && (
								<a href={form?.website?.startsWith('http') ? form.website : `https://${form.website}`} target='_blank' rel='noopener noreferrer' className='mt-1 inline-block text-indigo-600 dark:text-indigo-400 hover:underline text-center'>
									{form?.website}
								</a>
							)}
							<h2 className='mt-3 text-xl font-semibold text-gray-800 dark:text-gray-200'>{form?.title}</h2>
						</div>
					</header>
					<span className='text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap'>{form?.description}</span>

					{form?.acceptResponse && form?.formData?.length !== 0 ? (
						<div className='flex flex-col gap-6'>
							{form?.formData?.map((item, index) => (
								<>
									<Input2 key={item._id || index} type={item.type === 'dropdown' ? 'drop' : item.type == 'multi-select' ? 'mdrop' : item.type} fieldName={item.name} state={formData} options={item.options} returnKey={item._id} setState={setFormData} className='w-full' />
									{((typeof formData?.[item?._id] === 'string' && formData?.[item?._id]?.toLowerCase()?.includes('other')) || (formData[`${item?._id}_other`] && formData[`${item?._id}_other`].length > 0)) && <Input2 type='text' fieldName={`${item.name} (Specify)`} state={formData} returnKey={`${item._id}_other`} setState={setFormData} className='w-full mt-2' />}
								</>
							))}
							{form?.paymentNeed && (
								<div className='flex items-center justify-between'>
									<p className='text-lg font-semibold text-gray-800 dark:text-gray-200'>Total Amount: ₹{form?.amount}</p>
									<Button type='submit' onClick={handleStartPayment} className='h-12 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold rounded-md transition'>
										Pay Now
									</Button>
								</div>
							)}
							{!form?.paymentNeed && (
								<Button type='submit' on onClick={handleSubmit} className='w-full h-12 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white font-semibold rounded-md transition'>
									{loading ? 'Submitting...' : 'Submit'}
								</Button>
							)}
						</div>
					) : (
						<div className='bg-[#f4f4f4] dark:bg-[#1e1e28] rounded-2xl shadow-xl p-8 text-center'>
							<p className='text-lg font-semibold text-red-600 dark:text-red-400'>Form is not accepting responses</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default Forms;
